import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { ElasticFormModal, Grid, GridBody, GridCell, GridHeader, GridHeaderCell, HooksService } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import { MAX_CSV_BASIC_SIZE_MB } from 'actions/modules/datalake/common';
import { csvBasicEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import FileDetails from 'basic/FileDetails';

import StatusInfo from './StatusInfo';
import { STATUS_MODES } from './constants';

import { useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import './BasicCSVEditor.scss';

const MAX_CSV_BASIC_SIZE_BYTES = MAX_CSV_BASIC_SIZE_MB * 1000 * 1000
const ctrlKey = 17, rightCmdKey = 93, leftCmdKey = 91, sKey = 83
const ROW_HEIGHT = 32
const COLUMN_WIDTH = 150
const CSV = "csv"
const STEPS_NUMBER = 1000
const BasicCSVEditor = () => {
  const dispatch = useDispatch(),
        { id: fileId, editable, limit, newFile, groupName: groupNameLoc, provider } = useSelector(state => state.toolsState.csvBasicEditorState),
        [fileInfo, setFileInfo] = useState({}),
        [errors, setErrors] = useState([]),
        [isEditable, setIsEditable] = useState(false),
        [isParsingFinished, setIsParsingFinished] = useState(false),
        [areFileDetailsEditable, setAreFileDetailsEditable] = useState(newFile),
        [progress, setProgress] = useState(0),
        [statusMode, setStatusMode] = useState(STATUS_MODES.DOWNLOAD),
        windowSize = HooksService.useWindowSize(),
        getMetadataRequest = useRef(),
        ctrlDown = useRef(false),
        formRef = useRef(),
        tableContent = useRef({
          columns: [],
          rows: [],
          rowsCount: 0
        }),
        tableContentString = useRef(""),
        parsingWorker = useMemo(() => new Worker(new URL("./csvParsingWorker.js", import.meta.url)),[]),
        unParsingWorker = useMemo(() => new Worker(new URL("./csvUnParsingWorker.js", import.meta.url)),[]),
        checkWorker = useMemo(() => new Worker(new URL("./csvCheckWorker.js", import.meta.url)),[]),
        reqId = useMemo(() => CommonService.generateUID(),[]),
        cellCounter = useMemo(() => (
          <When condition={isParsingFinished}>
            <div className="ng-basic-csv-editor__counter">
              {fileInfo?.size < MAX_CSV_BASIC_SIZE_BYTES ? 
              `Columns: ${tableContent.current?.columns.length} Rows: ${tableContent.current?.rows.length}` 
              : 
              `Columns: ${tableContent.current?.columns.length} Rows limited to: ${tableContent.current?.rows.length} File exceeds ${MAX_CSV_BASIC_SIZE_MB} MB`}
            </div>
          </When>
        ), [fileInfo, isParsingFinished]),
        getHeight = useCallback(() => {
          const windowHeight = windowSize.height
          const rowsHeight = tableContent.current?.rows.length * ROW_HEIGHT
          const tableHeight = rowsHeight > windowHeight ? windowHeight * 0.7 - 170 : rowsHeight // 170px = modal header + modal footer 
          return tableHeight
        }, [windowSize]),
        onGridCellChange = useCallback((event, rowIndex, colIndex) => {
          tableContent.current.rows[rowIndex][colIndex] = event.target.value
        }, []),
        gridBodyCellRenderer = useCallback(({ rowIndex, columnIndex, ...props }) => (
          <GridCell
              {...props}
              columnIndex={columnIndex}
              defaultValue={tableContent.current?.rows[rowIndex][columnIndex] || ""}
              editable={isEditable}
              inputProps={{ id: CommonService.generateUID() }}
              onChange={onGridCellChange}
              rowIndex={rowIndex}
          />
        ), [isEditable, onGridCellChange]),
        gridHeaderCellRenderer = useCallback(({ columnIndex, rowIndex, ...props }) => (
          <GridHeaderCell
              {...props}
              columnIndex={columnIndex}
              rowIndex={rowIndex}
          >
            {String(tableContent.current?.columns[columnIndex])}
          </GridHeaderCell>
        ),[]),
        submitProps = useMemo(() => ({
          disabled: !editable || !isEditable || !isParsingFinished
        }), [editable, isEditable, isParsingFinished]),
        close = useCallback(() => {
          batch(() => {
            dispatch(csvBasicEditorActions.onChange(undefined, 'id'))
            dispatch(csvBasicEditorActions.onChange(false, 'open'))
            dispatch(csvBasicEditorActions.onChange(false, 'newFile'))
            dispatch(csvBasicEditorActions.onChange(false, 'editable'))
            dispatch(csvBasicEditorActions.onChange(undefined, 'provider'))
          })
        }, [dispatch]),
        onParsingError = useCallback(reject => {
          dispatch(globalActions.getMessageStore()).error("CSV file is damaged and can not be parsed")
          dispatch(globalActions.handleRequest({ id: reqId, isResponse: true }))
          close()
          reject()
        },[close, dispatch, reqId]),
        onFirstChunk = useCallback(fileStatus => {
          if(fileStatus?.isEditable) setIsEditable(true)
          if(fileStatus?.rowsCount) tableContent.current.rowsCount = fileStatus.rowsCount
          if(Array.isArray(fileStatus.columns)) tableContent.current.columns = fileStatus.columns
          dispatch(globalActions.handleRequest({ id: reqId, isResponse:true }))
        }, [dispatch, reqId]),
        onChunk = useCallback(chunk => {
          tableContent.current.rows.push(...chunk)
          setProgress(prevProgress => prevProgress + 1)
        },[]),
        onLastChunk = useCallback(resolve => {
          parsingWorker.terminate()
          setProgress(0)
          setStatusMode(STATUS_MODES.CHECK)
          return new Promise(resolve => {
            dispatch(globalActions.handleRequest({ id: reqId }))
            checkWorker.postMessage({ columnsLength: tableContent.current?.columns.length, rows: tableContent.current?.rows })            
            checkWorker.onmessage = ({ data: { isTableValid, invalidRows } }) => {
              if(!isTableValid){
                dispatch(globalActions.getMessageStore()).error("CSV file has different rows length and could be displayed improperly.")
                dispatch(globalActions.getMessageStore()).error(`Invalid ${invalidRows.length > 1 ? "rows indexes" : "row index"}: ${invalidRows?.join(",")}.`)
              }
              resolve()
            }
          }).finally(() => {
            checkWorker.terminate()
            dispatch(globalActions.handleRequest({ id: reqId, isResponse:true }))
            setIsParsingFinished(true)
            resolve()
          })
        }, [checkWorker, dispatch, parsingWorker, reqId]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(async response => {
              const location = response?.headers?.get("Location")
              const parsedData = await response.json()
              return ReduxService.updateObject(parsedData, { location })
            })
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, enabled: !newFile && !!fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.location, [infoResponse]),
        isPending = useRef(false),
        downloadFile = useCallback(() => {
          dispatch(globalActions.handleRequest({ id: reqId }));
          isPending.current = true
          new Promise((resolve, reject) => {
            parsingWorker.postMessage({ location, maxSize: MAX_CSV_BASIC_SIZE_BYTES, steps: STEPS_NUMBER })
            parsingWorker.onerror = e => {
              console.error(e)
              dispatch(globalActions.getMessageStore()).error("There was error during CSV file parsing") 
            }
            parsingWorker.onmessage = ({ data: { rowsPart, fileStatus } }) => {
              const isFirstChunkWithFileDetails = fileStatus && Object.keys(fileStatus).length === 4,
                    isStandardChunk = Array.isArray(rowsPart) 
            
              if(fileStatus?.isDamaged) onParsingError(reject) // parsing error 
              if(isFirstChunkWithFileDetails) onFirstChunk(fileStatus)
              if(isStandardChunk) onChunk(rowsPart)
              const isLastChunk = tableContent.current?.rows.length === tableContent.current?.rowsCount && tableContent.current?.rows.length > 0
              if(isLastChunk) onLastChunk(resolve)
            }
          }).finally(() => {
            dispatch(globalActions.handleRequest({ id: reqId, isResponse: true }))  
            isPending.current = false
          })
        }, [dispatch, reqId, parsingWorker, location, onParsingError, onFirstChunk, onChunk, onLastChunk]),
        optionalText = useMemo(() => limit !== undefined || Number.parseFloat((fileInfo?.size || 0)) > MAX_CSV_BASIC_SIZE_BYTES ? `Preview (up to ${MAX_CSV_BASIC_SIZE_MB}MB of data)` : undefined, [fileInfo, limit]),
        fileHeader = useMemo(() => (
          <When condition={isParsingFinished || newFile}>
            <FileDetails
                areFileDetailsEditable={areFileDetailsEditable}
                className="ng-csv-editor__top"
                editable={editable}
                errors={errors}
                extension={CSV}
                fileInfo={fileInfo}
                groupNameLoc={groupNameLoc}
                newFile={newFile}
                ref={formRef}
                setAreFileDetailsEditable={setAreFileDetailsEditable}
                setFileInfo={setFileInfo}
                />
          </When>
        ), [areFileDetailsEditable, editable, errors, fileInfo, groupNameLoc, isParsingFinished, newFile]),
        cleanTableContent = useCallback(() => {
          tableContent.current = undefined
        }, []),
        cleanTableContentString = useCallback(() => {
          tableContentString.current = undefined
        },[]),
        getGridContentAsCsv = useCallback(() =>
        new Promise((resolve, reject) => {
          let step = 0
          const arrayStep = Math.ceil(tableContent.current.rows.length / STEPS_NUMBER),
                onPostChunk = () => {
                  unParsingWorker.postMessage({ 
                    rows: tableContent.current.rows.slice(0, Math.max(arrayStep, 1)),
                    step,
                  })
                  tableContent.current.rows.splice(0, Math.max(arrayStep, 1)) 
                },
                onPostLastChunk = () => {
                  unParsingWorker.postMessage({ 
                    isFinished: true
                  })
                },
                onReceivedChunk = ({ tableAsString, receivedStep }) => {
                  if(tableAsString){
                    setProgress(prevProgress => prevProgress + 1)
                    if(receivedStep === 0){
                      dispatch(globalActions.handleRequest({ id: reqId, isResponse:true }))
                      tableContentString.current = tableContent.current.columns.join(",") + "\n" + tableAsString + "\n"
                    } else if (receivedStep > 0){
                      tableContentString.current = tableContentString.current + "\n" + tableAsString
                    }
                  }
    
                  if(receivedStep === step - 1){
                    setProgress(0)
                    setStatusMode(STATUS_MODES.UPLOAD)
                    cleanTableContent()
                    unParsingWorker.terminate()
                    resolve(tableContentString.current)
                  }
                }
          
          // sending messages to worker
          while (tableContent.current.rows.length > 0) {
            onPostChunk()
            step++
          }
          if(tableContent.current.rows.length === 0){
            onPostLastChunk() 
          }
          // receiving messages from worker
          unParsingWorker.onmessage = ({ data: { tableAsString, receivedStep } }) => {
            onReceivedChunk({ tableAsString, receivedStep })
          }

          unParsingWorker.onerror = () => {
            reject("There was an error during parsing csv to string")
          }
        
        }), [cleanTableContent, dispatch, reqId, unParsingWorker]),
        queryClient = useQueryClient(),
        getQueryBuilderUpload = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(({ response }) => setErrors(response?.validationErrors || []))
            .withFetching(false)
        , [dispatch]),
        { simpleUpload } = filesDatalakeQueries.useFileUpload({ 
          getQueryBuilder: getQueryBuilderUpload, 
          groupName: fileInfo.groupName, 
          fileName: fileInfo.fileName, 
          fileType: fileInfo.fileType,
          fields: Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v }))
        }),
        onSubmit = useCallback(() => {
          if(isPending.current) {
            return Promise.resolve();
          }
          if(!editable) {
            return Promise.resolve();
          } 
          if(areFileDetailsEditable) {
            formRef.current?.validateForForm()
            if(!formRef.current?.isValid()) {
              return Promise.resolve();
            }
          }
          dispatch(globalActions.handleRequest({ id: reqId }))
          setStatusMode(STATUS_MODES.PREPARE)
          setIsParsingFinished(false)

          const uploadFileCall = image => 
            simpleUpload({ image })
              .then(() => {
                dispatch(globalActions.getMessageStore()).success(newFile ? "New file has been saved." : "New version of file has been saved.")
                close()
                setIsParsingFinished(true)
                cleanTableContentString()
                setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
              })

          return getGridContentAsCsv().then(uploadFileCall).finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [editable, areFileDetailsEditable, dispatch, reqId, getGridContentAsCsv, simpleUpload, newFile, close, cleanTableContentString, queryClient]),
        onKeyDown = useCallback(e => {
          if(e.keyCode === 13 || e.keyCode === 27) {
            e.preventDefault()
            e.stopPropagation()
          }
          if(e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) {
            ctrlDown.current = true
          }
          if(ctrlDown.current && e.keyCode === sKey) {
            e.preventDefault()
            onSubmit()
          }
        }, [onSubmit]),
        onKeyUp = useCallback(e => {
          if(e.keyCode === ctrlKey || e.keyCode === leftCmdKey || e.keyCode === rightCmdKey) {
            ctrlDown.current = false
          }
        }, []),
        clearCtrl = useCallback(() => ctrlDown.current = false, [])

  useEffect(() => {
    if(!newFile && fileId && location) {
      downloadFile(fileId)
    }
  }, [downloadFile, fileId, location, newFile])

  useEffect(() => {
    if(Object.keys(infoResponse || {}).length > 0) {
      setFileInfo(infoResponse)
    }
  }, [infoResponse])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "basic_csv_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => () => {
    getMetadataRequest.current?.cancel()
    cleanTableContent()
    cleanTableContentString()
    checkWorker.terminate()
    parsingWorker.terminate()
    unParsingWorker.terminate()
    dispatch(globalActions.handleRequest({ id: reqId, isResponse:true }))
  }, [checkWorker, cleanTableContent, cleanTableContentString, dispatch, parsingWorker, reqId, unParsingWorker])

  return (
    <ElasticFormModal
        additionalFooterContent={cellCounter}
        className="ng-basic-csv-editor"
        closeOnBackground={false}
        closeOnEsc={false}
        onBlur={clearCtrl}
        onClose={close}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onSubmit={onSubmit}
        optionalText={optionalText}
        submitProps={submitProps}
        tabIndex="0"
        title={fileHeader}
    >
      <When condition={!isParsingFinished}>
        {() => (
          <StatusInfo 
              mode={statusMode}
              progress={progress}
              width={(windowSize.width - 64) * 0.7}
          />
        )}
      </When>
      <When condition={isParsingFinished}>
        {() => (
          <Grid>
            <GridHeader
                cellRenderer={gridHeaderCellRenderer}
                columnCount={tableContent.current?.columns.length}
                columnWidth={COLUMN_WIDTH}
                height={ROW_HEIGHT}
                width={(windowSize.width - 64) * 0.7 - 12} 
            />
            <GridBody
                cellRenderer={gridBodyCellRenderer}
                columnCount={tableContent.current?.columns.length}
                columnWidth={COLUMN_WIDTH}
                height={getHeight()}
                rowCount={tableContent.current?.rows.length}
                rowHeight={ROW_HEIGHT}
                width={(windowSize.width - 64) * 0.7}
            />
          </Grid>
        )}
      </When>
    </ElasticFormModal>
  )
}

export default memo(BasicCSVEditor);
